import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import messages from "../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import Breadcrumbs from "../../../components/BreadCrumbs";
import CompanyActivityLogView from "../../../components/ActivityLog/CompanyActivityLogView";
import {
  COMPANY_ACTIVITY_MODULE_BY_API_KEY,
  toActivityLogRow,
} from "../../../components/Dashboard/RecentActivityLog/constants";
import { getCompanyPortalActivityLogs } from "../../../network/apis/companyPortalDashboard";
import { getCompanyActivityLogsRequest } from "../../../store/CompanyPortal/ActivityLogs/actions";
import { getCompanyRolesRequest } from "../../../store/CompanyPortal/Roles/actions";

/** Role filter value for the company owner (PROLINE-3925) — not a company role. */
const COMPANY_OWNER_ROLE_ID = "company_owner";

const toRow = (entry) =>
  toActivityLogRow(entry, COMPANY_ACTIVITY_MODULE_BY_API_KEY);

/**
 * Company portal "Activity Log" (Full Logs) — the logged-in company's full
 * activity log (GET /company_portal/v1/activity_logs, PROLINE-3925). The
 * screen itself is `CompanyActivityLogView` (also the Super Admin company
 * profile's "Logs" tab).
 */
const CompanyActivityLog = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { list, meta, loading } = useSelector(
    (state) => state.companyActivityLogs,
  );
  const companyRoles = useSelector((state) => state.companyRoles.list);
  const { activityLog: copy, dashboard } = messages[lang];

  useEffect(() => {
    dispatch(getCompanyRolesRequest({ page: -1 }));
  }, [dispatch]);

  // Company roles + a static "Company Owner" option.
  const roleOptions = useMemo(
    () => [
      { id: COMPANY_OWNER_ROLE_ID, name: copy.company.filters.companyOwner },
      ...companyRoles.map(({ id, name }) => ({ id, name })),
    ],
    [companyRoles, copy.company.filters.companyOwner],
  );

  const onQueryChange = useCallback(
    (params) => dispatch(getCompanyActivityLogsRequest(params)),
    [dispatch],
  );

  const rows = useMemo(() => list.map(toRow), [list]);

  /** Export: every entry matching the current filters (`page=-1`). */
  const loadExportEntries = async (filterParams) => {
    try {
      const response = await getCompanyPortalActivityLogs({
        ...filterParams,
        page: -1,
      });
      return (response.data?.data || []).map(toRow);
    } catch {
      // The request interceptor already shows the error toast.
      return null;
    }
  };

  return (
    <div className="activity-log">
      <Breadcrumbs
        list={[
          { path: ROUTE_PATHS.companyDashboard, label: dashboard.breadcrumb },
          { path: "", label: copy.title },
        ]}
        BreadCrumbHasBackIcon={false}
      />
      <CompanyActivityLogView
        rows={rows}
        count={meta.count}
        pages={meta.pages}
        loading={loading}
        roleOptions={roleOptions}
        onQueryChange={onQueryChange}
        loadExportEntries={loadExportEntries}
      />
    </div>
  );
};

export default CompanyActivityLog;
